export interface LiveEventConfig {
  name: string;
  targetDate: Date;
  teaser: string;
}

const DEFAULT_NAME = "The Storm Surge Finale";
const DEFAULT_TEASER = "The storm is closing in… be there.";

function lerpHex(a: string, b: string, t: number): string {
  const parse = (hex: string) => {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
  };
  const ca = parse(a);
  const cb = parse(b);
  const r = Math.round(ca[0] + (cb[0] - ca[0]) * t);
  const g = Math.round(ca[1] + (cb[1] - ca[1]) * t);
  const bv = Math.round(ca[2] + (cb[2] - ca[2]) * t);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${bv.toString(16).padStart(2, "0")}`;
}

export class FpsLiveEvent {
  private _config: LiveEventConfig;
  private _countdownEl: HTMLDivElement | null = null;
  private _teaserOverlay: HTMLDivElement | null = null;
  private _teaserShown = false;
  private _mount: HTMLElement;

  constructor(mount: HTMLElement, config?: LiveEventConfig) {
    this._mount = mount;
    if (config) {
      this._config = config;
    } else {
      this._config = {
        name: DEFAULT_NAME,
        targetDate: new Date(Date.now() + 7 * 24 * 3600 * 1000),
        teaser: DEFAULT_TEASER,
      };
    }
  }

  update(_dt: number): void {
    this._refreshCountdown();

    if (!this._teaserShown && this.isEventLive()) {
      this._teaserShown = true;
      this.showTeaser();
    }
  }

  attachCountdownTo(el: HTMLElement): void {
    const div = document.createElement("div");
    div.style.cssText =
      "font-family:monospace;color:#fff;font-size:14px;letter-spacing:0.05em;";
    el.appendChild(div);
    this._countdownEl = div;
    this._refreshCountdown();
  }

  getSkyTint(): string {
    const msUntil = this._config.targetDate.getTime() - Date.now();
    const farFraction = Math.max(0, Math.min(1, msUntil / (7 * 86400 * 1000)));
    // farFraction=1 → far away (#0a0e1a), farFraction=0 → close/live (#3a0a5e)
    return lerpHex("#3a0a5e", "#0a0e1a", farFraction);
  }

  isEventLive(): boolean {
    const msUntil = this._config.targetDate.getTime() - Date.now();
    return Math.abs(msUntil) <= 60_000;
  }

  showTeaser(): void {
    if (this._teaserOverlay) return;

    const overlay = document.createElement("div");
    overlay.style.cssText = [
      "position:fixed",
      "inset:0",
      "z-index:9500",
      "background:rgba(0,0,0,0.92)",
      "display:flex",
      "flex-direction:column",
      "align-items:center",
      "justify-content:center",
      "gap:24px",
      "font-family:monospace",
      "color:#fff",
    ].join(";");

    const title = document.createElement("h1");
    title.textContent = this._config.name;
    title.style.cssText =
      "font-size:clamp(24px,5vw,56px);margin:0;text-align:center;text-shadow:0 0 30px #a855f7;";

    const teaser = document.createElement("p");
    teaser.textContent = this._config.teaser;
    teaser.style.cssText =
      "font-size:clamp(14px,2vw,22px);margin:0;opacity:0.85;text-align:center;";

    const countdown = document.createElement("div");
    countdown.style.cssText =
      "font-size:clamp(16px,2.5vw,28px);letter-spacing:0.1em;color:#a855f7;";

    const closeBtn = document.createElement("button");
    closeBtn.textContent = "Close";
    closeBtn.style.cssText = [
      "margin-top:16px",
      "padding:10px 32px",
      "font-size:16px",
      "font-family:monospace",
      "background:transparent",
      "color:#fff",
      "border:1px solid #fff",
      "border-radius:4px",
      "cursor:pointer",
    ].join(";");
    closeBtn.addEventListener("click", () => this.hideTeaser());

    overlay.appendChild(title);
    overlay.appendChild(teaser);
    overlay.appendChild(countdown);
    overlay.appendChild(closeBtn);

    this._teaserOverlay = overlay;
    this._mount.appendChild(overlay);

    // keep the countdown inside the teaser updated
    const tick = () => {
      if (!this._teaserOverlay) return;
      countdown.textContent = this._formatCountdown();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  hideTeaser(): void {
    if (this._teaserOverlay) {
      this._teaserOverlay.remove();
      this._teaserOverlay = null;
    }
  }

  // ── private helpers ────────────────────────────────────────────────────────

  private _refreshCountdown(): void {
    if (!this._countdownEl) return;
    this._countdownEl.textContent = this._formatCountdown();
  }

  private _formatCountdown(): string {
    const msUntil = this._config.targetDate.getTime() - Date.now();

    if (Math.abs(msUntil) <= 60_000) {
      return "LIVE NOW!";
    }

    if (msUntil < 0) {
      return `EVENT ENDED`;
    }

    const totalSec = Math.floor(msUntil / 1000);
    const d = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;

    return `EVENT in ${d}d ${h}h ${m}m ${s}s`;
  }
}
